'use client';

import { useEffect } from 'react';
import { isChunkLoadError } from '#/lib/chunk-load-recovery';
import {
  collectHydrationSignals,
  findDomMutator,
  isHydrationMismatch
} from '#/lib/hydration-mismatch';
import { reportClientError, toReport } from '#/lib/report-client-error';

/**
 * 잡히지 않은 브라우저 오류를 전부 주워서 신고한다. (#262)
 *
 * 에러 바운더리(`error.tsx`)는 **렌더 중** 예외만 잡는다. 이벤트 핸들러 안에서
 * 던진 예외, await 하지 않은 Promise 거절, 스크립트 로드 실패는 바운더리를
 * 그냥 지나쳐 콘솔에만 남는다 — 사용자는 "버튼이 안 눌려요" 를 겪지만 우리는
 * 아무 것도 모른다. 토스 SDK 초기화 실패가 정확히 그런 종류였다.
 *
 * 루트 레이아웃에 한 번 마운트한다. 노이즈 필터·중복 억제·유량 제한은
 * 서버(`/api/client-error`, `#/lib/error-alert`)에서 한다.
 */
export function ErrorReporter() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      // 이미지·스크립트 로드 실패도 같은 이벤트로 올라오는데 내용이 비어 있다.
      // 알릴 정보가 없으므로 버린다.
      if (!event.error && !event.message) return;
      // 청크 로드 실패는 오류 바운더리가 회복·보고를 전담한다(#273). 여기서도
      // 신고하면 같은 이벤트가 web/client 채널로 중복 알림된다.
      if (isChunkLoadError(event.error ?? event.message)) return;

      const error = event.error ?? event.message;
      const stack =
        event.error instanceof Error
          ? event.error.stack
          : `${event.filename ?? ''}:${event.lineno ?? 0}:${event.colno ?? 0}`;

      // 하이드레이션 불일치는 대개 브라우저 자동 번역이 본문을 바꿔서 난다.
      // 번역 흔적이 있으면 우리가 손댈 것이 없으므로 버리고, 없으면 우리 코드가
      // 원인일 수 있으니 스코프를 갈라 경고로 남긴다. 자세한 사정은
      // `#/lib/hydration-mismatch` 주석 참고. (#262)
      if (isHydrationMismatch(error)) {
        if (findDomMutator()) return;
        // 흔적이 없다고 우리 버그로 단정하지 않는다 — 흔적 목록은 브라우저
        // 버전을 따라 늙는다. 대신 판별에 필요한 값을 알림에 실어 보내고
        // 사람이 가른다. (#262)
        reportClientError(
          toReport('hydration', error, {
            scope: 'hydration',
            severity: 'warn',
            stack,
            signals: collectHydrationSignals()
          })
        );
        return;
      }

      reportClientError(toReport('client', error, { scope: 'client', stack }));
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      // 위와 같다 — dynamic import 거절도 바운더리 쪽 회복에 맡긴다. (#273)
      if (isChunkLoadError(event.reason)) return;
      reportClientError(
        toReport('unhandledrejection', event.reason, {
          scope: 'unhandledrejection'
        })
      );
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
